import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import { type Filter, type SegmentProductListMeta } from "src/segment";
import type { FilterAction } from "src/hooks/useSegment";
import type { SelectedFacets } from "src/types/adapters/listing.type";

export type ListingFilterRange = { min: number; max: number };

type ProductListFilteredEventProps = {
  meta: SegmentProductListMeta;
  searchState: SelectedFacets;
  filterAction?: FilterAction;
  filtersApplied?: Filter[];
  sortApplied?: string;
  numPage?: number;
  initPrice?: ListingFilterRange;
  resultId?: string;
};

type ProductListFilteredAnalyticsProps = {
  result_id?: string;
  filters: Filter[];
  list_format: string;
  list_id: string;
  list_type: string;
  path: string;
  shop_type?: string;
  filter_action?: FilterAction;
  filters_applied?: Filter[];
  sort_applied?: string;
  num_page?: number;
};

export type ProductListFilteredEvent = TrackingEvent<
  "Product List Filtered",
  ProductListFilteredAnalyticsProps
>;

function buildFiltersFromState(
  searchState: SelectedFacets,
  initPrice?: ListingFilterRange
): Filter[] {
  const filters: Filter[] = [];

  for (const [key, values] of Object.entries(searchState)) {
    if (!values?.length) continue;

    if (key === "price") {
      const [raw] = values;
      const [minRaw, maxRaw] = (raw ?? "").split(":");
      const min = minRaw ? Number(minRaw) : initPrice?.min;
      const max = maxRaw ? Number(maxRaw) : initPrice?.max;

      if (typeof min === "number" && min > 0) {
        filters.push({ type: "min_price", value: min });
      }
      if (typeof max === "number" && max > 0) {
        filters.push({ type: "max_price", value: max });
      }
      continue;
    }

    for (const value of values) {
      filters.push({ type: key, value });
    }
  }

  return filters;
}

export async function trackProductListFilteredEvent({
  meta,
  searchState,
  filterAction,
  filtersApplied,
  sortApplied,
  numPage,
  initPrice,
  resultId,
}: ProductListFilteredEventProps): TrackingEventResponse {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const filters = buildFiltersFromState(searchState, initPrice);

  return await trackEvent("Product List Filtered", {
    props: {
      result_id: resultId,
      filters,
      list_format: meta.listFormat,
      list_id: meta.listId,
      list_type: meta.listType,
      path,
      shop_type: meta.shopType,
      filter_action: filterAction,
      filters_applied: filtersApplied,
      sort_applied: sortApplied,
      num_page: numPage,
    },
    withRequestSource: true,
  });
}
